<template>
  <div>
    <FilterBar
      :filters="filterSchema"
      :value="filters"
      :search.sync="search"
      :date-range.sync="dateRange"
      :result-count="error ? null : totalCount"
      result-label="borrowers"
      search-placeholder="Search user ID, mobile or name"
      @change="reload"
      @reset="resetFilters"
    >
      <template #actions>
        <!-- Shared with the Logs tab: one meaning of "static" on the screen. -->
        <v-checkbox
          v-model="staticShown"
          label="See static"
          title="Messages the growth agent did not choose: welcome, disbursal and rejection messages, panel broadcasts, and every loan, payment, EMI and KYC message"
          dense
          hide-details
          class="kf-static mt-0 pt-0 mr-2"
        />
        <v-btn
          small
          text
          class="text-none"
          :disabled="!!loading"
          @click="load"
        >
          <LucideIcon name="refresh-cw" :size="15" class="mr-1" :spin="!!loading" />
          Refresh
        </v-btn>
      </template>
    </FilterBar>

    <DataTable
      class="mt-3 kf-user-history"
      title="Borrowers who received messages"
      :headers="headers"
      :items="items"
      :loading="loading"
      :error="error"
      :page="page"
      :total-pages="totalPages"
      :total-count="totalCount"
      :page-length="pageLength"
      item-key="user_id"
      show-expand
      :expanded.sync="expanded"
      empty-title="No borrowers"
      empty-message="Nobody received a message under these filters. Try widening the date range, ticking See static, or clearing the search."
      empty-icon="users"
      @page-change="onPage"
      @retry="load"
      @row-click="toggleRow"
    >
      <template #[`item.user_id`]="{ item }">
        <div class="kf-user">
          <span v-if="item.customer_name">{{ item.customer_name }}</span>
          <span v-else class="kf-muted">No name</span>
          <div class="kf-detail">ID {{ item.user_id }}</div>
        </div>
      </template>

      <template #[`item.mobile`]="{ item }">
        <span class="kf-nowrap">{{ item.mobile || "-" }}</span>
      </template>

      <template #[`item.received_count`]="{ item }">
        <div>
          <span class="kf-count">{{ fmt.number(item.received_count) }}</span>
          <div
            v-if="item.would_send_count"
            class="kf-detail"
            title="Picked by the agent while sends were in dry run: logged, not sent"
          >
            {{ fmt.number(item.would_send_count) }} dry run
          </div>
        </div>
      </template>

      <template #[`item.last_received_at`]="{ item }">
        <span class="kf-nowrap">{{ fmt.dateTimeIst(item.last_received_at) }}</span>
      </template>

      <!-- The words the borrower got, never the template's name. A subject
           (email) or push title is shown above it, since they saw that too. -->
      <template #[`item.last_body`]="{ item }">
        <div class="kf-last" :title="item.last_body || item.last_title">
          <div v-if="item.last_subject" class="kf-last__subject">
            {{ item.last_subject }}
          </div>
          <div class="kf-last__body">
            <LucideIcon
              :name="channelIcon(item.last_channel)"
              :size="14"
              class="kf-last__icon"
            />{{ item.last_body || item.last_title || "-" }}
          </div>
        </div>
      </template>

      <template #[`item.channels`]="{ item }">
        <div class="kf-channels">
          <span
            v-for="c in item.channels"
            :key="c.channel"
            class="kf-chip"
            :title="`${c.count} on ${channelLabel(c.channel)}`"
          >
            <LucideIcon :name="channelIcon(c.channel)" :size="12" />
            {{ channelLabel(c.channel) }} {{ c.count }}
          </span>
        </div>
      </template>

      <!-- The borrower's messages, open under their row. Keyed by the load
           as well, so a new filter, page or search re-fetches every list. -->
      <template #expanded-item="{ headers: cols, item }">
        <td :colspan="cols.length" class="kf-expanded">
          <UserMessagesInline
            :key="`${item.user_id}:${loadCount}`"
            :user-id="item.user_id"
            :filters="rowFilters"
          />
        </td>
      </template>
    </DataTable>
  </div>
</template>

<script>
/**
 * The Notification History's User history tab: one row per borrower who
 * received a message, most recently messaged first. Every row on the page
 * arrives open, showing the messages that borrower received under the same
 * filters, so the list under a row always totals the number the row shows.
 * Clicking a row closes or reopens it.
 *
 * "Received" means it went out: the provider accepted it, or under a dry run
 * the agent would have sent it. Skipped and failed messages stay on the Logs
 * tab.
 */
import DataTable from "@/components/karrofin/DataTable.vue";
import FilterBar from "@/components/karrofin/FilterBar.vue";
import UserMessagesInline from "@/components/karrofin/UserMessagesInline.vue";
import api from "@/app-js/karrofin/api";
import fmt from "@/app-js/karrofin/format";
import { CHANNELS } from "@/app-js/karrofin/constants";

/** How far back the tab looks on arrival. Clearing the date shows all time. */
const DEFAULT_DAYS = 30;

/** [from, to] as YYYY-MM-DD, the last `days` days including today. */
function lastDays(days) {
  const pad = (v) => String(v).padStart(2, "0");
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return [iso(from), iso(to)];
}

function emptyFilters() {
  return { channel: null };
}

export default {
  name: "KarroFinNotificationUserHistory",
  components: { DataTable, FilterBar, UserMessagesInline },
  props: {
    /** The screen's "See static" state, shared with the Logs tab (.sync). */
    showStatic: { type: Boolean, default: false },
  },
  data() {
    return {
      fmt,
      loading: false,
      error: null,
      items: [],
      page: 1,
      pageLength: 20,
      totalPages: 1,
      totalCount: null,
      search: "",
      dateRange: lastDays(DEFAULT_DAYS),
      filters: emptyFilters(),
      // The open rows: every row, after each load.
      expanded: [],
      // The filters the shown rows were loaded with. The lists under the rows
      // use these rather than the live bar, so a half-edited filter never
      // mixes with rows from the last one.
      rowFilters: {},
      // Counts finished loads; part of each open list's key.
      loadCount: 0,
      // Each load takes a ticket; a reply that is not the latest is dropped, so
      // a slow earlier request cannot overwrite the rows of a newer filter.
      ticket: 0,
      filterSchema: [
        {
          key: "channel",
          label: "Channel",
          type: "select",
          options: CHANNELS,
          itemText: "label",
          itemValue: "value",
          placeholder: "Any channel",
          pinned: true,
        },
      ],
      headers: [
        // Declared so the chevron sits at the LEFT of the borrower, where an
        // expandable row expects it. Vuetify only appends its own when absent.
        { text: "", value: "data-table-expand", width: 46 },
        { text: "User", value: "user_id" },
        { text: "Mobile", value: "mobile" },
        { text: "Received", value: "received_count" },
        { text: "Last received (IST)", value: "last_received_at" },
        { text: "Last message", value: "last_body" },
        { text: "Channels", value: "channels" },
      ],
    };
  },
  computed: {
    staticShown: {
      get() {
        return this.showStatic;
      },
      set(value) {
        this.$emit("update:showStatic", value);
      },
    },
  },
  created() {
    this.load();
  },
  watch: {
    showStatic() {
      this.reload();
    },
  },
  methods: {
    load() {
      const ticket = ++this.ticket;
      this.loading = true;
      this.error = null;
      // What a row's list applies, so it totals the row's "Received". Search
      // only picks borrowers; it does not narrow their messages.
      const rowFilters = {
        date_range: this.dateRange.length === 2 ? this.dateRange : undefined,
        channel: this.filters.channel || undefined,
        static: this.showStatic ? 1 : 0,
      };
      api
        .getAutomationHistoryUsers({
          ...rowFilters,
          page_number: this.page,
          page_length: this.pageLength,
          search: (this.search || "").trim() || undefined,
        })
        .then((data) => {
          if (ticket !== this.ticket) return;
          this.rowFilters = rowFilters;
          this.loadCount += 1;
          this.items = data.results;
          // Every row open. A fresh array, since it holds the row OBJECTS:
          // the old one would point at rows no longer on screen.
          this.expanded = data.results.slice();
          this.totalPages = data.total_page_count;
          this.totalCount = data.total_count;
          this.page = data.page_number;
        })
        .catch((err) => {
          if (ticket !== this.ticket) return;
          this.error = err;
          this.items = [];
          this.expanded = [];
        })
        .finally(() => {
          if (ticket === this.ticket) this.loading = false;
        });
    },
    reload() {
      this.page = 1;
      this.load();
    },
    onPage(page) {
      this.page = page;
      this.load();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    resetFilters() {
      this.filters = emptyFilters();
      this.search = "";
      this.dateRange = lastDays(DEFAULT_DAYS);
      if (this.showStatic) {
        // The watcher reloads.
        this.staticShown = false;
      } else {
        this.reload();
      }
    },
    /* v-data-table hands the click its own expand helper; the row click
       just flips it. */
    toggleRow(item, slotData) {
      if (slotData) slotData.expand(!slotData.isExpanded);
    },
    channelLabel(value) {
      const c = CHANNELS.find((x) => x.value === value);
      return c ? c.label : value || "-";
    },
    channelIcon(value) {
      const c = CHANNELS.find((x) => x.value === value);
      return c ? c.icon : "send";
    },
  },
};
</script>

<style scoped>
.kf-nowrap {
  white-space: nowrap;
}
.kf-muted {
  color: #54636f;
}
.kf-static {
  flex: 0 0 auto;
  white-space: nowrap;
}
.kf-user {
  min-width: 120px;
  line-height: 1.35;
}
.kf-count {
  font-weight: 700;
  color: #000;
}
.kf-channel {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}
.kf-last {
  max-width: 380px;
  min-width: 240px;
  line-height: 1.45;
}
.kf-last__subject {
  font-size: var(--kf-fs-meta);
  font-weight: 600;
  color: #54636f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Two lines of the message; the whole text is on hover. */
.kf-last__body {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: var(--kf-fs-hint);
  color: #1f2d36;
}
.kf-last__icon {
  margin-right: 5px;
  vertical-align: -2px;
}
.kf-channels {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 140px;
}
.kf-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 12px;
  background: #eef3f8;
  color: #1f2d36;
  font-size: var(--kf-fs-label);
  white-space: nowrap;
}
.kf-detail {
  margin-top: 2px;
  font-size: var(--kf-fs-label);
  color: #54636f;
  white-space: nowrap;
}
/* The whole row opens and closes the borrower's messages. */
.kf-user-history >>> tbody tr {
  cursor: pointer;
}
</style>
